import { useEffect, useState, useCallback } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import {
  Plus, PencilSimple, Trash, Check, X, ArrowUp, ArrowDown, Stack,
} from "@phosphor-icons/react";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";

export default function CampaignPillars({ campaignId }) {
  const [pillars, setPillars] = useState([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [newName, setNewName] = useState("");
  const [editingId, setEditingId] = useState(null);
  const [draft, setDraft] = useState({ name: "", description: "", funnel_stage: "" });
  const [busyId, setBusyId] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/campaigns/${campaignId}/pillars`);
      setPillars(data);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not load pillars");
      setPillars([]);
    } finally { setLoading(false); }
  }, [campaignId]);

  useEffect(() => { load(); }, [load]);

  const doCreate = async () => {
    const name = newName.trim();
    if (!name) return;
    setCreating(true);
    try {
      const { data } = await api.post(`/campaigns/${campaignId}/pillars`, { name });
      setPillars(all => [...all, data]);
      setNewName("");
      toast.success(`Added "${data.name}"`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Add failed");
    } finally { setCreating(false); }
  };

  const startEdit = (p) => {
    setEditingId(p.pillar_id);
    setDraft({ name: p.name || "", description: p.description || "", funnel_stage: p.funnel_stage || "" });
  };

  const saveEdit = async (p) => {
    const name = draft.name.trim();
    if (!name) { toast.error("Name is required"); return; }
    setBusyId(p.pillar_id);
    try {
      const { data } = await api.patch(`/pillars/${p.pillar_id}`, {
        name, description: draft.description, funnel_stage: draft.funnel_stage,
      });
      setPillars(all => all.map(x => x.pillar_id === p.pillar_id ? data : x));
      setEditingId(null);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Save failed");
    } finally { setBusyId(null); }
  };

  const doDelete = async (p) => {
    if (!window.confirm(`Remove "${p.name}"?`)) return;
    setBusyId(p.pillar_id);
    try {
      await api.delete(`/pillars/${p.pillar_id}`);
      setPillars(all => all.filter(x => x.pillar_id !== p.pillar_id));
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Delete failed");
    } finally { setBusyId(null); }
  };

  const move = async (p, dir) => {
    const sorted = [...pillars].sort((a, b) => a.sort_order - b.sort_order);
    const idx = sorted.findIndex(x => x.pillar_id === p.pillar_id);
    const swap = dir === "up" ? idx - 1 : idx + 1;
    if (swap < 0 || swap >= sorted.length) return;
    const other = sorted[swap];
    setBusyId(p.pillar_id);
    try {
      // Swap sort_order values
      const [a, b] = [p.sort_order, other.sort_order];
      await Promise.all([
        api.patch(`/pillars/${p.pillar_id}`, { sort_order: b }),
        api.patch(`/pillars/${other.pillar_id}`, { sort_order: a }),
      ]);
      setPillars(all => all.map(x => {
        if (x.pillar_id === p.pillar_id) return { ...x, sort_order: b };
        if (x.pillar_id === other.pillar_id) return { ...x, sort_order: a };
        return x;
      }));
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Reorder failed");
    } finally { setBusyId(null); }
  };

  const ordered = [...pillars].sort((a, b) => a.sort_order - b.sort_order);

  return (
    <div className="space-y-4" data-testid="pillars-panel">
      <div className="flex items-start gap-3">
        <div>
          <h4 className="text-[11px] uppercase tracking-[0.24em] text-ink-secondary/80 flex items-center gap-1.5">
            <Stack weight="duotone" className="h-3 w-3" />
            Content pillars
          </h4>
          <p className="mt-1 text-xs text-ink-secondary max-w-xl">
            The recurring content themes for this campaign (e.g. Educational, Case Studies, Behind the Scenes).
            Add as many as you need — reorder to signal priority.
          </p>
        </div>
      </div>

      <div className="flex items-center gap-2">
        <Input data-testid="pillar-new-name" value={newName}
          onChange={(e) => setNewName(e.target.value)}
          onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); doCreate(); } }}
          placeholder="e.g. Educational" maxLength={200}
          className="bg-graphite-950 border-white/10 text-sm" />
        <button data-testid="pillar-add" onClick={doCreate} disabled={creating || !newName.trim()}
          className="inline-flex items-center gap-2 rounded-full bg-white text-graphite-950 px-4 py-2 text-sm font-medium hover:bg-neutral-100 disabled:opacity-60">
          <Plus weight="bold" className="h-4 w-4" /> {creating ? "Adding…" : "Add"}
        </button>
      </div>

      <div className="rounded-md border border-white/10 bg-graphite-950/40 overflow-hidden divide-y divide-white/5" data-testid="pillars-list">
        {loading ? (
          <div className="p-6 text-center text-sm text-ink-secondary">Loading pillars…</div>
        ) : ordered.length === 0 ? (
          <div className="p-6 text-center text-sm text-ink-secondary">No pillars yet. Add the first one above.</div>
        ) : ordered.map((p, i) => {
          const editing = editingId === p.pillar_id;
          const busy = busyId === p.pillar_id;
          return (
            <div key={p.pillar_id} data-testid={`pillar-row-${p.pillar_id}`} className="p-4 space-y-2">
              {editing ? (
                <>
                  <Input data-testid={`pillar-edit-name-${p.pillar_id}`} value={draft.name}
                    onChange={(e) => setDraft({ ...draft, name: e.target.value })} maxLength={200}
                    className="bg-graphite-900 border-white/10 text-sm" />
                  <Textarea data-testid={`pillar-edit-description-${p.pillar_id}`} value={draft.description}
                    onChange={(e) => setDraft({ ...draft, description: e.target.value })} rows={2}
                    placeholder="Description (optional)" className="bg-graphite-900 border-white/10 text-sm" />
                  <Input data-testid={`pillar-edit-stage-${p.pillar_id}`} value={draft.funnel_stage}
                    onChange={(e) => setDraft({ ...draft, funnel_stage: e.target.value })}
                    placeholder="Funnel stage (optional) — e.g. Top of funnel"
                    className="bg-graphite-900 border-white/10 text-sm" />
                  <div className="flex items-center gap-2 justify-end">
                    <button onClick={() => setEditingId(null)}
                      className="text-[11px] text-ink-secondary hover:text-ink-primary inline-flex items-center gap-1">
                      <X weight="bold" className="h-3 w-3" /> Cancel
                    </button>
                    <button data-testid={`pillar-save-${p.pillar_id}`} onClick={() => saveEdit(p)} disabled={busy}
                      className="inline-flex items-center gap-1 rounded-full bg-white text-graphite-950 px-3 py-1 text-xs font-medium disabled:opacity-60">
                      <Check weight="bold" className="h-3 w-3" /> Save
                    </button>
                  </div>
                </>
              ) : (
                <div className="flex items-start gap-3">
                  <div className="flex flex-col gap-0.5 pt-0.5">
                    <button data-testid={`pillar-up-${p.pillar_id}`} onClick={() => move(p, "up")} disabled={busy || i === 0}
                      className="text-ink-secondary hover:text-ink-primary disabled:opacity-30" title="Move up">
                      <ArrowUp weight="bold" className="h-3 w-3" />
                    </button>
                    <button data-testid={`pillar-down-${p.pillar_id}`} onClick={() => move(p, "down")} disabled={busy || i === ordered.length - 1}
                      className="text-ink-secondary hover:text-ink-primary disabled:opacity-30" title="Move down">
                      <ArrowDown weight="bold" className="h-3 w-3" />
                    </button>
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span data-testid={`pillar-name-${p.pillar_id}`} className="text-sm text-ink-primary">{p.name}</span>
                      {p.funnel_stage && (
                        <span className="text-[10px] uppercase tracking-widest text-ink-secondary/80 border border-white/10 rounded-full px-2 py-0.5">
                          {p.funnel_stage}
                        </span>
                      )}
                    </div>
                    {p.description && (
                      <p className="mt-1 text-xs text-ink-secondary leading-relaxed">{p.description}</p>
                    )}
                  </div>
                  <div className="flex items-center gap-1">
                    <button data-testid={`pillar-edit-${p.pillar_id}`} onClick={() => startEdit(p)} disabled={busy}
                      className="p-1.5 rounded-md text-ink-secondary hover:text-ink-primary hover:bg-white/5 disabled:opacity-60">
                      <PencilSimple weight="bold" className="h-3.5 w-3.5" />
                    </button>
                    <button data-testid={`pillar-delete-${p.pillar_id}`} onClick={() => doDelete(p)} disabled={busy}
                      className="p-1.5 rounded-md text-ink-secondary hover:text-accent-danger hover:bg-white/5 disabled:opacity-60">
                      <Trash weight="bold" className="h-3.5 w-3.5" />
                    </button>
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
